import {
  HostedPaymentSessionPayload,
  HostedPaymentSessionRequest,
  MinorUnits,
  Nullable,
  PaymentAttribute,
  PaymentMethod,
  toMinorUnitsEpic,
} from '@appliedsystems/payments-core';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { usePaymentsTranslation } from 'src/hooks/usePaymentsTranslation';
import { getConfig } from 'src/util/config';
import { createContainer } from 'unstated-next';
import { useFeatureFlags } from '../..//hooks/useFeatureFlag';
import { ApiClient } from '../../api/ApiClient';
import { currencyMap } from '../../constants/constants';
import { Locale } from '../../store/Locale';
import { getAmountWithFees, toIntlFormatHelper } from '../../util/getAmountWithFees';
import { ErrorMessage } from '../ErrorAlert/ErrorAlert';
import { availablePaymentMethods } from '../PaymentMethodSelection/PaymentMethodSelection';
import { PayBySelection } from './enums';
import { FullInvoiceGroup, HppData, RetrievedInvoices } from './types';
import { useHppDataStore } from './useHppData';

export type HppSession = Pick<
  HostedPaymentSessionPayload,
  | 'sessionData'
  | 'expiresAt'
  | 'sessionId'
  | 'pspSessionId'
  | 'paymentFlowSessionId'
  | 'allowedPaymentMethods'
  | 'appliedProductId'
  | 'useVarAchFee'
  | 'useVarEftFee'
  | 'appliedSplitBasisPoints'
> &
  Nullable<Pick<HostedPaymentSessionPayload, 'creditFeePercent' | 'feeAch' | 'feeCredit' | 'feeEft'>>;
// todo PAY-2239, currently this is the same as HostedPaymentSessionPayload.
// add needed info from PspSessionResponsePayload to create a new combined type

const useHppSession = () => {
  const locale = Locale.useContainer().locale;
  const { pathname } = useLocation();
  const { hppData, setHppData, paymentMethodConfig } = useHppDataStore();
  const { isPaymentMethodCheckoutBEnabled } = useFeatureFlags();
  const { t } = usePaymentsTranslation();

  const [hppSessionRequest, setHppSessionRequest] = useState<HostedPaymentSessionRequest>();
  const [hppSession, setHppSession] = useState<HppSession>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<ErrorMessage>();

  useEffect(() => {
    // Set checkoutBEnabled based on not PF, feature flag and randomizer
    setHppData({
      checkoutBEnabled: !hppData.pf?.quote?.downPaymentAmount && !!isPaymentMethodCheckoutBEnabled,
    });
  }, [isPaymentMethodCheckoutBEnabled, setHppData, hppData.pf?.quote?.downPaymentAmount]);

  const initializeHppPaymentSession = async (
    hppToken: string,
    hppData: HppData,
    retrievedInvoices: RetrievedInvoices,
    selectedInvoices: FullInvoiceGroup[],
  ): Promise<boolean> => {
    if (pathname.includes('checkoutv2')) {
      // Should not allow a session to be iniated from the checkout page
      return false;
    }

    try {
      const selectedInvoiceNumbers = selectedInvoices.map((e) => e.invoiceNumber);

      const transactions = retrievedInvoices
        .filter((e) => selectedInvoiceNumbers.includes(e.invoiceNumber))
        .map((invoice) => {
          return {
            id: invoice.transactionId,
            amount: {
              units: Number(invoice.amountDue.units),
              partialUnits: Number(invoice.amountDue.partialUnits),
            },
            classCode: invoice.classCode,
            itemNumber: invoice.itemNumber,
            invoiceNumber: invoice.invoiceNumber,
          };
        });

      const selectedInvoiceItems = selectedInvoices.flatMap((invoice) => invoice.transactions);

      const splitPaymentAttributes =
        hppData.payBy === PayBySelection.INVOICE
          ? selectedInvoiceItems.map((invoice) => {
              return [
                {
                  name: PaymentAttribute.InvoiceNumber,
                  value: invoice.invoiceNumber,
                },
                {
                  name: PaymentAttribute.AccountNumber,
                  value: hppData.accountCode,
                },
                {
                  name: PaymentAttribute.PaymentAmount,
                  value: toMinorUnitsEpic(invoice.amountDue, currencyMap[locale]).toString(),
                },
                {
                  name: PaymentAttribute.Description,
                  value: invoice.description,
                },
              ];
            })
          : [];

      const paymentAttributes: {
        name: PaymentAttribute;
        value: string;
      }[] = [];

      if ([PayBySelection.AMOUNT, PayBySelection.NONE].includes(hppData.payBy)) {
        if (hppData.invoiceNumber) {
          paymentAttributes.push({
            name: PaymentAttribute.InvoiceNumber,
            value: hppData.invoiceNumber,
          });
        }
        if (hppData.policyNumber) {
          paymentAttributes.push({
            name: PaymentAttribute.PolicyNumber,
            value: hppData.policyNumber,
          });
        }
        if (hppData.accountCode) {
          paymentAttributes.push({
            name: PaymentAttribute.AccountNumber,
            value: hppData.accountCode,
          });
        }
        if (hppData.paymentAmount) {
          paymentAttributes.push({
            name: PaymentAttribute.PaymentAmount,
            value: hppData.paymentAmount.toString(),
          });
        }
      }

      const data: HostedPaymentSessionRequest = {
        customer: {
          firstName: hppData.firstName,
          lastName: hppData.lastName,
          email: hppData.userEmail,
          businessName: hppData.businessName,
        },
        locale,
        amount: hppData.paymentAmount,
        paymentDescription: hppData.paymentDescription,
        workflow: hppData.paymentWorkflow,
        paymentAttributes,
        splitPaymentAttributes,
        ...(hppData.clientId && {
          epicInvoiceMeta: {
            clientId: hppData.clientId,
            ...(transactions.length && {
              transactions,
            }),
          },
        }),
        pfQuickQuoteId: hppData.pf?.quickQuote?.pfQuickQuoteId,
        checkoutBEnabled: hppData.checkoutBEnabled,
      };

      // Update the session data
      setHppSessionRequest(data);

      // Set loading state to true
      setIsSubmitting(true);
      setErrorMessage(undefined);

      // Determine if we have changed the payment data
      if (hppSessionRequest && JSON.stringify(hppSessionRequest) === JSON.stringify(data) && hppSession) {
        // Don't make a duplicate session if user submitted the same data twice
        setIsSubmitting(false);
        return true;
      }

      setHppSession(undefined);

      const response = await ApiClient.getInstance().initHppSession(hppToken, data);

      // Stop loading once the API call is done
      setIsSubmitting(false);

      if (response.status === 'ok') {
        setHppSession(response.data);
        let nextHppData: Partial<HppData> = {};

        nextHppData.pf = {
          ...hppData.pf,
          quote: response.data?.pfQuoteResult?.pfQuote ?? undefined,
          errors: response.data?.pfQuoteResult?.errors ?? null,
        };

        const defaultMethod = availablePaymentMethods
          .filter((method) => response.data?.allowedPaymentMethods.includes(method) ?? true)
          .map((key) => ({ key, ...paymentMethodConfig[key] }))
          .find((c) => c.allowed);
        if (defaultMethod) {
          const { paymentTotal, paymentFee } = getAmountWithFees(
            defaultMethod.key,
            hppData.paymentAmount,
            defaultMethod.key === PaymentMethod.Ach
              ? response.data!.feeAch
              : defaultMethod.key === PaymentMethod.Card
              ? response.data!.creditFeePercent
              : (paymentMethodConfig as any)[defaultMethod.key].fee,
            currencyMap[locale],
            response.data?.useVarAchFee,
            response.data?.useVarEftFee,
          );

          nextHppData = { ...nextHppData, paymentMethod: defaultMethod.key, paymentFee, paymentTotal };
        }

        setHppData(nextHppData);
        return true;
      }

      if (response.type === 'network' || response.status > 500) {
        setErrorMessage(['GET_HPP_SESSION_ERROR_NETWORK']);
      } else if (response.status === 500) {
        setErrorMessage(['GET_HPP_SESSION_ERROR_INTERNAL', { id: response.traceId }]);
      } else {
        setErrorMessage(['GET_HPP_SESSION_ERROR_UNKNOWN', { id: response.traceId }]);
      }

      // Failed to initialize session
      return false;
    } catch (err) {
      console.error('Failed to get HPP session with error', err);
      setErrorMessage(['GET_HPP_SESSION_ERROR_UNKNOWN']);
      setIsSubmitting(false);
      return false;
    }
  };

  const getFeePercentOrAmount = useCallback(
    (method: PaymentMethod, subtotal: MinorUnits, fee: MinorUnits): string => {
      const showFeeAsPercentage =
        [PaymentMethod.Card].includes(method) ||
        (method === PaymentMethod.Ach && hppSession?.useVarAchFee && fee) ||
        (method === PaymentMethod.Eft && hppSession?.useVarEftFee && fee);
      let percentage = (fee / (Number(subtotal) || 1.0)) * 100 * 100;
      if ([PaymentMethod.Card].includes(method) && hppSession) {
        if (typeof hppSession.creditFeePercent === 'number') percentage = hppSession.creditFeePercent;
        else if (hppSession.appliedSplitBasisPoints)
          percentage = Math.min(hppSession.appliedSplitBasisPoints, percentage);
      }
      if (method === PaymentMethod.Ach && hppSession?.useVarAchFee)
        percentage = Number(getConfig('REACT_APP_VAR_ACH_PERCENTAGE'));
      if (method === PaymentMethod.Eft && hppSession?.useVarEftFee)
        percentage = Number(getConfig('REACT_APP_VAR_EFT_PERCENTAGE'));
      const feeAsPercentage = (percentage / 100).toFixed(1);
      let feeText = showFeeAsPercentage ? feeAsPercentage + '%' : toIntlFormatHelper(fee, currencyMap[locale], locale);

      if (method === PaymentMethod.Ach && hppSession?.useVarAchFee && fee)
        feeText += t('VAR_FEE_COMMA_MAX_OF_X', undefined, {
          x: toIntlFormatHelper(Number(getConfig('REACT_APP_VAR_ACH_MAX_FEE')), currencyMap[locale], locale),
        } as any).replace('.00', '');
      if (method === PaymentMethod.Eft && hppSession?.useVarEftFee && fee)
        feeText += t('VAR_FEE_COMMA_MAX_OF_X', undefined, {
          x: toIntlFormatHelper(Number(getConfig('REACT_APP_VAR_EFT_MAX_FEE')), currencyMap[locale], locale),
        } as any).replace('.00', '');

      return feeText;
    },
    [hppSession?.creditFeePercent, hppSession?.useVarAchFee, hppSession?.useVarEftFee, locale, t],
  );

  return {
    hppSession,
    setHppSession,
    isSubmitting,
    initializeHppPaymentSession,
    errorMessage,
    getFeePercentOrAmount,
  };
};

export const HppSessionStore = createContainer(useHppSession);
export const useHppSessionStore = () => {
  return HppSessionStore.useContainer();
};
